import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2 } from "lucide-react";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  const paymentType = searchParams.get('type');
  const amount = searchParams.get('amount');
  const context = searchParams.get('context');

  // Check if user has flight compensation data saved
  const hasFlightData = () => {
    try {
      const savedClaim = localStorage.getItem('flight-compensation-claim');
      const savedEligibility = localStorage.getItem('flight-eligibility-results');
      return !!(savedClaim || savedEligibility);
    } catch {
      return false;
    }
  };

  const getRedirectPath = () => {
    if (paymentType === 'one-time') {
      // Redirect to pricing page since flight compensation is disabled
      return '/pricing';
    }
    return '/dashboard';
  };

  useEffect(() => {
    // Auto-redirect to pricing after 5 seconds for one-time payments
    if (paymentType === 'one-time') {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate(getRedirectPath());
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [paymentType, navigate]);

  const handleContinue = () => {
    const redirectPath = getRedirectPath();
    navigate(redirectPath);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <CardTitle className="text-2xl text-green-600">Payment Successful!</CardTitle>
          <CardDescription>
            Your payment has been processed successfully.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {amount && (
            <div className="text-center">
              <p className="text-lg font-semibold">Amount: ${amount}</p>
              <p className="text-sm text-muted-foreground">
                {paymentType === 'one-time' ? 'One-time Payment' : 'Subscription Payment'}
              </p>
            </div>
          )}
          
          {paymentType === 'one-time' && (
            <div className="text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                {hasFlightData() 
                  ? 'Continuing to your flight claim generation...'
                  : 'You now have access to flight compensation claim generation.'
                }
              </p>
              <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>
                  {hasFlightData() 
                    ? `Continuing in ${countdown} seconds...`
                    : `Redirecting to Flight Claims in ${countdown} seconds...`
                  }
                </span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Button 
              onClick={handleContinue} 
              className="w-full"
              variant="default"
            >
              {paymentType === 'one-time' ? 'Continue to Flight Claims' : 'Go to Dashboard'}
            </Button>
            <Button 
              onClick={() => navigate('/dashboard')} 
              variant="outline" 
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;